import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SheetTitle } from "../../../../components/ui/sheet";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import Spinner from "../../../../components/spinner.components";
import type { IPagination } from "../../../../types/models/pagination.models.types";
import PaginationComponent from "../../../../components/pagination.components";
import { useGetPostsAdminHttp } from "../../../../hooks/http/admin/posts/get-posts.admin.http";
import { ExternalLink } from "lucide-react";
import type { IPost } from "../../../../types/models/post.models.types";

export default function ManageVendorCampaignPostsAdminPartial() {
  const { vendorId, campaignId } = useParams();
  const { toast } = useToast();

  const { fetching, execute: getPostsHttp } = useGetPostsAdminHttp();

  const [posts, setPosts] = useState<IPost[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [paginationData, setPaginationData] = useState<IPagination>();

  async function getPosts(page: number) {
    if (!vendorId || !campaignId) return;

    const response = await getPostsHttp({
      body: null,
      queryParams: {
        userId: vendorId,
        campaignId,
        page,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    setPosts((prevData) => {
      const newPosts = response.data.posts.filter((post) => {
        return !prevData.find((p) => p.id === post.id);
      });

      return [...prevData, ...newPosts];
    });

    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (vendorId && campaignId) {
      getPosts(pageNo);
    }
  }, [pageNo, vendorId, campaignId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setPosts([]);
    setPageNo(1);
  }, [vendorId, campaignId]);

  // Calculate total posts count
  const totalPosts = paginationData?.totalPosts || posts.length;

  if (posts.length === 0 && fetching) {
    return (
      <div className="flex flex-col w-full h-full">
        <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
          Manage Posts
        </SheetTitle>
        <div className="flex flex-col flex-1 justify-center items-center">
          <Spinner width={20} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full">
      <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
        Manage Posts
      </SheetTitle>

      <div className="flex flex-col flex-1 gap-6 p-5 overflow-y-auto">
        {/* Total Posts Header */}
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white text-xl">Total posts</span>
          <span className="bg-tertiarybuttonbg px-3 py-1 border border-border rounded-full font-medium text-sm">
            {totalPosts}
          </span>
        </div>

        {/* Posts List */}
        {posts.length === 0 ? (
          <div className="flex flex-col flex-1 justify-center items-center">
            <p className="text-center text-secondary text-sm">No posts found</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {posts.map((post) => (
              <div
                key={post.id}
                className="flex justify-between items-center bg-tertiarybuttonbg p-6 border border-border rounded-lg"
              >
                {/* Post Details */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white">
                      @{post.social?.handle}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {post.liveUrl ? (
                      <a
                        href={post.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="max-w-80 truncate">
                          {post.liveUrl}
                        </span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                {/* Amount */}
                <div className="flex flex-col items-start gap-1 px-10 border-border border-l">
                  <span className="text-secondary text-sm">Amount</span>
                  <span className="font-semibold text-left text-lg text-white">
                    ₹ {post.fixedPayout?.amount?.toLocaleString("en-IN") || "-"}
                  </span>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {paginationData && (
              <PaginationComponent
                fetching={fetching}
                paginationData={paginationData}
                onClick={() => setPageNo((currentPage) => currentPage + 1)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
